import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { CollectionFormApiService } from 'src/app/api/bizzmine/collection-form/collection-form-api.service';
import { IndexedDBService } from '../indexed-db/indexed-db.service';
import { BizzMineLocalStorageService } from 'src/app/shared/services/localStorage/bizzmine-local-storage.service';
import { DocumentTreeApiService } from 'src/app/api/bizzmine/document-tree/document-tree-api.service';
import { GridSearchFilterSearchDto } from 'src/models/ts/grid-search-filter-search-dto.model';
import { CollectionListApiService } from 'src/app/api/bizzmine/collection-list/collection-list-api.service';
import { PwaSyncModel } from 'src/models/ts/pwa-sync.model';
import { CollectionForm } from 'src/models/ts/collection-form.model';
import { PwaApiService } from 'src/app/api/bizzmine/pwa/pwa-api.service';
import { OfflineSyncType } from 'src/app/shared/components/grid/cells/grid-cell-extension/grid-cell-extension.component';
import { PwaSyncType } from 'src/models/ts/pwa-sync-type.model';
import { PwaService } from '../pwa/pwa.service';
import { SyncQueueService } from 'src/app/core/services/sync/sync-queue.service';
import { environment } from 'src/environments/environment';
import { PwaSyncUpdateType } from 'src/models/ts/pwa-sync-update-type.model';
import { DocumentTreeNode } from 'src/app/features/bizzmine/widgets/document-tree-widget/document-tree-widget.component';

export interface PwaOfflineFolders {
  folderId: number,
  folderName?: string, // optional, used for display purposes
  listId: number,
  widgetId: number,
  collectionFormId: number,
  collectionId: number,
  instances: {
    instanceId: number,
    versionId: number,
    instanceLastModifiedOn?: Date,
    formLastModifiedOn?: Date,
    status: OfflineSyncType
  }[],
  status: OfflineSyncType,
  lastModifiedOn: Date;
}

export interface PwaOfflineInstances {
  collectionId: number,
  instanceId: number,
  versionId: number,
  listId?: number,
  widgetId?: number,
  collectionFormId: number,
  status: OfflineSyncType,
  instanceLastModifiedOn: Date,
  formLastModifiedOn: Date,
}

export interface PwaOfflineItems {
  instances: Array<PwaOfflineInstances>;
  folders: Array<PwaOfflineFolders>;
}

@Injectable({
  providedIn: 'root'
})
export class KeepOfflineService {
  public offlineItems: PwaOfflineItems;

  // Inject dependencies using the inject function
  private collectionFormApiService = inject(CollectionFormApiService);
  private indexeddbService = inject(IndexedDBService);
  private bizzmineLocalStorage = inject(BizzMineLocalStorageService);
  private documentTreeApiService = inject(DocumentTreeApiService);
  private collectionListApiService = inject(CollectionListApiService);
  private pwaApiService = inject(PwaApiService);
  private pwaService = inject(PwaService);
  private syncQueueService = inject(SyncQueueService);

  public constructor() {
    const offlineItemsCache = this.bizzmineLocalStorage.getItem('offlineItems');
    if (!offlineItemsCache) {
      this.offlineItems = {
        instances: [],
        folders: []
      };
      this.setLocalCache();
    } else {
      this.offlineItems = JSON.parse(offlineItemsCache);
    }
  }

  public setFailedInstance(collectionId: number, instanceId: number): void {
    this.syncQueueService.setInstanceState(collectionId, instanceId, OfflineSyncType.Failed);
    const instance = this.offlineItems.instances.find((i: PwaOfflineInstances) => i.collectionId === collectionId && i.instanceId === instanceId);
    if (instance) {
      instance.status = OfflineSyncType.Failed;
    }
    this.setLocalCache();
  }


  public setDownloadingInstance(collectionId: number, instanceId: number, versionId: number, widgetId: number, listId: number): void {
    this.syncQueueService.setInstanceState(collectionId, instanceId, OfflineSyncType.Downloading);
    const instance = this.offlineItems.instances.find((i: PwaOfflineInstances) => i.collectionId === collectionId && i.instanceId === instanceId && i.versionId === versionId);
    if (!instance) {
      this.offlineItems.instances = this.offlineItems.instances.filter((i) => !(i.collectionId === collectionId && i.instanceId === instanceId && i.versionId !== versionId));
      this.offlineItems.instances.push({
        collectionId,
        instanceId,
        versionId,
        widgetId,
        listId,
        status: OfflineSyncType.Downloading
      } as PwaOfflineInstances);
    } else {
      instance.status = OfflineSyncType.Downloading;
    }
    this.setLocalCache();
  }

  public setDownloadingFolder(folderId: number, widgetId: number, listId: number, collectionId: number): void {
    const folder = this.offlineItems.folders.find((f: PwaOfflineFolders) => f.folderId === folderId);
    if (!folder) {
      this.offlineItems.folders.push({
        folderId,
        widgetId,
        listId,
        collectionId,

        status: OfflineSyncType.Downloading,
        lastModifiedOn: new Date()
      } as PwaOfflineFolders);
    } else {
      folder.status = OfflineSyncType.Downloading;
      folder.lastModifiedOn = new Date();
    }
    this.setLocalCache();
  }

  public addOfflineInstance(collectionId: number, instanceId: number, collectionFormId: number, instanceLastModifiedOn: Date, formLastModifiedOn: Date): void {
    const instance = this.offlineItems.instances.find((i: PwaOfflineInstances) => i.collectionId === collectionId && i.instanceId === instanceId);
    if (instance) {
      instance.status = OfflineSyncType.Downloaded;
      instance.collectionFormId = collectionFormId;
      instance.instanceLastModifiedOn = instanceLastModifiedOn;
      instance.formLastModifiedOn = formLastModifiedOn;
      this.setLocalCache();
    }
  }

  public removeOfflineInstance(collectionId: number, instanceId: number, versionId: number, folderId: number | undefined): void {
    this.pwaApiService
      .unsyncInstance(collectionId, instanceId, versionId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.deleteAllInstancesInOfflineItems(collectionId, instanceId, versionId, folderId);
          this.deleteDataInIndexedDb(collectionId, instanceId, versionId);
          this.syncQueueService.setInstanceState(collectionId, instanceId, OfflineSyncType.None);
        },
        error: (error) => {
          console.error(`Error unsyncing instance ${instanceId} of collection ${collectionId}:`, error);
        }
      });
  }

  public deleteInstanceCache(collectionId: number, instanceId: number, versionId: number, folderId: number | undefined, all: boolean): void {
    if (all) {
      this.deleteAllInstancesInOfflineItems(collectionId, instanceId, versionId, folderId);
    } else {
      this.deleteInstanceInOfflineItems(collectionId, instanceId, versionId, folderId);
    }
    this.deleteDataInIndexedDb(collectionId, instanceId, versionId, folderId);
    this.syncQueueService.setInstanceState(collectionId, instanceId, OfflineSyncType.None);
  }


  public addOfflineFolder(folderId: number, status: OfflineSyncType): void {
    const folderData = this.offlineItems.folders.find((folder: PwaOfflineFolders) => folder.folderId === folderId);
    if (folderData) {
      folderData.status = status;

      if (!this.offlineItems.folders.some((folder) => folder.folderId === folderData.folderId)) {
        this.offlineItems.folders.push(folderData);
      }
      this.setLocalCache();
    }
  }

  public deleteOfflineFolder(folderIds: number[]): void {
    this.offlineItems.folders = this.offlineItems.folders.filter((folder: PwaOfflineFolders) => !folderIds.includes(folder.folderId));
    this.setLocalCache();
  }

  public removeOfflineFolder(folderIds: number[]): void {
    const folders = this.offlineItems.folders.filter((folder: PwaOfflineFolders) => folderIds.includes(folder.folderId));
    // Delete cached data for each instance in the folders being unsynced
    folders.forEach((folder: PwaOfflineFolders) => {
      folder.instances?.forEach((instance) => {
        this.deleteDataInIndexedDb(folder.collectionId, instance.instanceId, instance.versionId, folder.folderId);
        this.syncQueueService.setInstanceState(folder.collectionId, instance.instanceId, OfflineSyncType.None);
      });
    });
    this.pwaApiService.unsyncFolder(this.parsOfflineItemsToPwaPayload({
      folders: folders,
      instances: []
    })).pipe(take(1)).subscribe({
      next: () => {
        this.offlineItems.folders = this.offlineItems.folders.filter((folder: PwaOfflineFolders) => !folderIds.includes(folder.folderId));
        this.setLocalCache();
      },
      error: (error) => {
        console.error('Error unsyncing offline folders:', error);
      }
    });

  }

  //create a function that add instance to the offline items cache of folder
  public addOfflineFolderInstance(instanceId: number, versionId: number, collectionFormId: number | undefined, instanceLastModifiedOn: Date | undefined, formLastModifiedOn: Date | undefined, folderId: number, status: OfflineSyncType): void {
    const folderData = this.offlineItems.folders.find((folder: PwaOfflineFolders) => folder.folderId === folderId);
    if (folderData) {
      if (folderData.collectionFormId == undefined) {
        folderData.collectionFormId = collectionFormId as number;
      }
      if(!folderData.instances){
        folderData.instances = [];
      }
      const instance = folderData.instances.find((i) => i.instanceId === instanceId && i.versionId === versionId);
      if (!instance) {
        folderData.instances = folderData.instances.filter((i) => !(i.instanceId === instanceId && i.versionId !== versionId))
        folderData.instances.push({
          instanceId: instanceId,
          versionId: versionId,
          instanceLastModifiedOn: instanceLastModifiedOn,
          formLastModifiedOn: formLastModifiedOn,
          status: status
        });
      } else {
        instance.instanceLastModifiedOn = instanceLastModifiedOn;
        instance.formLastModifiedOn = formLastModifiedOn;
        instance.status = status;
      }
      this.setLocalCache();
    }
  }

  public createFormByInstanceReadResponseObservable(
    collectionId: number,
    instanceId: number,
    versionId: number,
    guidChecksum: string | undefined,
    pwaSyncType: PwaSyncType,
    pwaSyncUpdateType: PwaSyncUpdateType,
  ): Observable<any> {
    let totalProgress = 0;
    let currentProgress = 0;
    let formData: CollectionForm;

    return this.collectionFormApiService
      .getFormByInstanceReadResponse(collectionId, instanceId, versionId, guidChecksum, pwaSyncType, pwaSyncUpdateType, true)
      .pipe(
        catchError((error) => {
          const enhancedError = {originalError: error, data: {collectionId, instanceId, versionId}};
          return throwError(() => enhancedError);
        }),
        tap((res: { url: string; body: any } | null) => {
          if (res != null) {
            formData = res.body;
            // Pwasynctype and updateTypes are used for audit logging in the backend,
            // but on the frontend, we always want to use the same cached key stored in IndexedDB.
            res.url = res.url.replace(/pwasynctype\/\d+(\/updatetypes\/\d+)?/, 'pwasynctype/0/updatetypes/0');
            this.saveDataInIndexedDb(res.url, res.body);
          }
          currentProgress++;
        }),
        map((result: any) => ({
          formData,
          result,
          totalProgress: totalProgress,
          currentProgress: currentProgress
        }))
      );
  }

  public createFormByInstanceReadWithPreviewFileResponseObservable(
    collectionId: number,
    instanceId: number,
    versionId: number,
    guidChecksum: string | undefined,
    folderId: number | undefined,
    pwaSyncType: PwaSyncType,
    pwaUpdateType: PwaSyncUpdateType
  ): Observable<any> {
    let totalProgress = 0;
    let currentProgress = 0;
    let formData: CollectionForm;
    return this.collectionFormApiService
      .getFormByInstanceReadResponse(collectionId, instanceId, versionId, guidChecksum, pwaSyncType, pwaUpdateType, true)
      .pipe(
        catchError((error) => {
          const enhancedError = { originalError: error, data: { collectionId, instanceId, versionId, folderId } };
          return throwError(() => enhancedError);
        }),
        tap((res: { url: string; body: any } | null) => {
          if (res != null) {
            formData = res.body;
            const url = `collection/${collectionId}/instance/${instanceId}/version/${versionId}/readonly`.appendChecksum(guidChecksum);
            this.pwaService.fetchRoute(url);
            // Pwasynctype and updateTypes are used for audit logging in the backend,
            // but on the frontend, we always want to use the same cached key stored in IndexedDB.
            res.url = res.url.replace(/pwasynctype\/\d+(\/updatetypes\/\d+)?/, 'pwasynctype/0/updatetypes/0');
            this.saveDataInIndexedDb(res.url, res.body);
          }
          currentProgress++;
        }),
        switchMap((res: { body: { DocumentProperties?: { File?: number } } } | null) => {
          totalProgress++;
          if (res?.body?.DocumentProperties?.File === 0) return of(null);
          //download original file & preview file if exists
          return this.pwaApiService.downloadOfflineFile({
            collectionsId: collectionId,
            instancesId: instanceId
          }, true);
        }),
        catchError((error) => {
          const enhancedError = {
            originalError: error,
            data: {
              collectionId,
              instanceId,
              versionId,
              folderId,
              collectionFormId: formData.CollectionFormId,
              instanceLastModifiedOn: formData.InstanceLastModifiedOn,
              formLastModifiedOn: formData.FormLastModifiedOn
            }
          };
          return throwError(() => enhancedError);
        }),
        tap((res: any) => {
          if (res != null) {
            this.saveDataInIndexedDb(res.url, res.body);
          }
          currentProgress++;
        }),
        map((result: any) => ({
          formData,
          result,
          folderId: folderId,
          totalProgress: totalProgress,
          currentProgress: currentProgress
        }))
      );
  }

  public createListSearchObservableFromAllInstances(collectionId: number, instanceId: number, versionId: number): Observable<unknown>[] {
    const folders = this.offlineItems.folders.filter(f => f.instances.some(i => i.instanceId === instanceId && i.versionId === versionId));
    const instance = this.offlineItems.instances.find(i => i.collectionId === collectionId && i.instanceId === instanceId && i.versionId === versionId);
    const observables: Observable<unknown>[] = [];
    folders.forEach(folder => {
      if(folder.listId) {
        observables.push(this.saveFolderListSearchObservable(folder.listId, folder.folderId, true));
      }
    });

    if(instance && instance.listId && instance.widgetId){
      observables.push(this.saveListSearchObservable(instance.listId, instance.widgetId, true));
    }
    return observables;
  }

  public saveDataInIndexedDb(url: string, data: any): void {
    this.indexeddbService.saveData('dataStore', { id: this.getRelativeUrl(url), data: data, lastModified: new Date() });
  }

  private hasInstance(collectionId: number, instanceId: number, versionId: number): boolean {
    return !!this.offlineItems.instances?.find((i) => i.collectionId === collectionId && i.instanceId === instanceId && i.versionId === versionId);
  }

  public deleteDataInIndexedDb(collectionId: number, instanceId: number, versionId: number, folderId?: number): void {
    // Check if there is other instances of the same collection still offline
    let hasOtherInstance: boolean;
    if (folderId) {
      hasOtherInstance = this.hasInstance(collectionId, instanceId, versionId) || !!this.offlineItems.folders.find(f => f.folderId !== folderId && f.instances?.some((i) => i.instanceId === instanceId && i.versionId === versionId));
    } else {
      hasOtherInstance = !!this.offlineItems.folders.find(f => f.folderId !== folderId && f.instances?.some((i) => i.instanceId === instanceId && i.versionId === versionId));
    }

    if (hasOtherInstance) return;

    // Delete cached data in indexedDB for the given collection, instance, and version
    const readData = `api/collection/${collectionId}/instance/${instanceId}/version/${versionId}`;
    const fileData = `api/pwa/sync/download/collection/${collectionId}/instance/${instanceId}`;
    this.indexeddbService.deleteDataInStoreWhereKeyContains('dataStore', readData).then((res) => {
      this.indexeddbService.deleteDataInStoreWhereKeyContains('dataStore', fileData).then((res) => {
      });
    });
  }

  public deleteFolderStructureInIndexedDb(widgetId: number, folderNode: DocumentTreeNode): void {
    this.indexeddbService.deleteDataInStoreWhereKeyContains('dataStore', `api/documenttree/${widgetId}/folder/${folderNode.ID}/children`).then((res) => {
      this.indexeddbService.deleteDataInStoreWhereKeyContains('dataStore', `api/collection/list/${folderNode.ListsID}/folder/${folderNode.ID}/search`).then((res) => {
      });
    });
  }

  public offlineInstanceCached(collectionId: number, instanceId: number, versionId: number, isFolder: boolean = false): boolean {
    let item = this.offlineItems.folders.find((f: any) => f.collectionId === collectionId && f.instances?.some((i: any) => i.instanceId === instanceId && i.versionId === versionId))?.instances.find((i: any) => i.instanceId === instanceId && i.versionId === versionId);
    if(item == undefined) {
      item = this.offlineItems.instances.find((f: any) => f.collectionId === collectionId && f.instanceId === instanceId && f.versionId === versionId);
    }
    return item?.status === OfflineSyncType.Downloaded;
  }

  public offlineInstanceDownloading(collectionId: number, instanceId: number, versionId: number, isFolder: boolean): boolean {
    let item;
    if (isFolder) {
      item = this.offlineItems.folders.find((f: any) => f.collectionId === collectionId && f.instances?.some((i: any) => i.instanceId === instanceId && i.versionId === versionId))?.instances.find((i: any) => i.instanceId === instanceId && i.versionId === versionId);
    }
    if(item == undefined) {
      item = this.offlineItems.instances.find((f: any) => f.collectionId === collectionId && f.instanceId === instanceId);
    }
    return item?.status === OfflineSyncType.Downloading;
  }

  public offlineInstanceFailed(collectionId: number, instanceId: number, versionId: number, isFolder: boolean): boolean {
    let item;
    if (isFolder) {
      item = this.offlineItems.folders?.find((f: any) => f.collectionId === collectionId && f.instances?.some((i: any) => i.instanceId === instanceId && i.versionId === versionId))?.instances.find((i: any) => i.instanceId === instanceId && i.versionId === versionId);
    }
    if(item == undefined) {
      item = this.offlineItems.instances.find((f: any) => f.collectionId === collectionId && f.instanceId === instanceId);
    }
    return item?.status === OfflineSyncType.Failed;
  }


  public offlineFolderCached(folderId: number): boolean {
    const item = this.offlineItems.folders.find((f: any) => f.folderId === folderId);
    return item?.status === OfflineSyncType.Downloaded;
  }

  public offlineFolderDownloading(folderId: number): boolean {
    const item = this.offlineItems.folders.find((f: any) => f.folderId === folderId);
    return item?.status === OfflineSyncType.Downloading;
  }

  public offlineFolderFailed(folderId: number): boolean {
    const item = this.offlineItems.folders.find((f: any) => f.folderId === folderId);
    return item?.status === OfflineSyncType.Failed;
  }

  public parsOfflineItemsToPwaPayload(offlineItems: PwaOfflineItems = this.offlineItems): PwaSyncModel {
    return {
      Instances: offlineItems.instances.filter(item => item.status !== OfflineSyncType.Failed).map((item) => ({
        CollectionId: item.collectionId,
        InstanceId: item.instanceId,
        VersionId: item.versionId,
        ViewListId: item.listId,
        WidgetId: item.widgetId,
        FormId: item.collectionFormId,
        InstanceLastModifiedOn: item.instanceLastModifiedOn,
        FormLastModifiedOn: item.formLastModifiedOn
      })),
      Folders: offlineItems.folders.map((folder) => ({
        FolderId: folder.folderId,
        WidgetId: folder.widgetId,
        FormId: folder.collectionFormId,
        CollectionId: folder.collectionId,
        ListsId: folder.listId,
        Instances: folder.instances?.filter(item => item.status !== OfflineSyncType.Failed).map((instance) => ({
          InstanceId: instance.instanceId,
          VersionId: instance.versionId,
          InstanceLastModifiedOn: instance.instanceLastModifiedOn,
          FormLastModifiedOn: instance.formLastModifiedOn
        }))
      }))
    };
  }

  public deleteAllInstancesInOfflineItems(collectionId: number, instanceId: number, versionId: number, folderId: number | undefined): void{
    this.removeInstanceFromOfflineItems(collectionId, instanceId, versionId);
    this.removeInstanceFromFolderOfflineItems(instanceId, versionId, folderId, true);
    this.setLocalCache();
  }

  public deleteInstanceInOfflineItems(collectionId: number, instanceId: number, versionId: number, folderId: number | undefined): void {
    if(folderId){
      // Remove the instances in the folders.instances
      this.removeInstanceFromFolderOfflineItems(instanceId, versionId, folderId, false);
    }else{
      // Remove the instance from the instances array
      this.removeInstanceFromOfflineItems(collectionId, instanceId, versionId);
    }
    this.setLocalCache();
  }

  private removeInstanceFromOfflineItems(collectionId: number, instanceId: number, versionId: number): void {
    this.offlineItems.instances = this.offlineItems.instances.filter(
      (i: any) => !(i.collectionId === collectionId && i.instanceId === instanceId && i.versionId === versionId)
    );
  }

  private removeInstanceFromFolderOfflineItems(instanceId: number, versionId: number, folderId: number | undefined, allFolders: boolean): void {
    this.offlineItems.folders = this.offlineItems.folders.map((folder: PwaOfflineFolders) => {
      if(folder.folderId !== folderId && !allFolders) return folder;
      folder.instances = folder.instances?.filter((instance) => !(instance.instanceId === instanceId && instance.versionId === versionId));
      return folder;
    });
  }




  public saveFolderListSearchObservable(listId: number, folderId: number, canViewHiddenDocuments: boolean): Observable<any> {
    return this.documentTreeApiService.getFolderDataResponse(listId, folderId, {
      CanViewHiddenDocuments: canViewHiddenDocuments,
      ListID: listId,
      Records: 999999
    }).pipe(
      catchError((error) => {
        console.error('Error fetching folder data:', error);
        return of(null); // Return null or handle the error as needed
      }),
      tap((res) => {
        if (res != null && res.url != null && res.body != null) {
          this.saveDataInIndexedDb(res.url, res.body);
        }
      })
    );
  }

  public saveListSearchObservable(listId: number, widgetId: number, canViewHiddenDocuments: boolean): Observable<any> {
    const searchDto: Partial<GridSearchFilterSearchDto> = {
      ListID: listId,
      WidgetID: widgetId,
      CanViewHiddenDocuments: canViewHiddenDocuments
    };
    return this.collectionListApiService.listSearchResponse(listId, searchDto).pipe(
      catchError((error) => {
        console.error('Error fetching list search response:', error);
        return of(null); // Return null or handle the error as needed
      }),
      tap((res) => {
        if (res != null && res.url != null && res.body != null) {
          this.saveDataInIndexedDb(res.url, res.body);
        }
      })
    );
  }




  private getRelativeUrl(url: string): string {
    if (!url) {
      console.warn('No url');
      return '';
    }
    return url.replace(new RegExp(environment.apiUri + '/'), '');
  }

  private setLocalCache(): void {
    this.bizzmineLocalStorage.setItem('offlineItems', JSON.stringify(this.offlineItems));
  }
}
